import { bearingDeg } from "@/lib/geo";

export type LatLng = {
  lat: number;
  lng: number;
};

export type PinBucket =
  | "front-left"
  | "front-center"
  | "front-right"
  | "middle-left"
  | "middle-center"
  | "middle-right"
  | "back-left"
  | "back-center"
  | "back-right";

export type TargetType = "pin" | "center" | "layup";

export type HoleTargetPreset = {
  courseId: string;
  hole: number;
  holeAzimuthDeg?: number;
  greenCenter?: LatLng;
  teeGround?: LatLng;
  layup?: LatLng;
  pinBuckets?: Partial<Record<PinBucket, LatLng>>;
};

export const HOLE_TARGET_PRESETS: HoleTargetPreset[] = [
  // holeAzimuthDeg values below are the EXACT bearing computed from real
  // teeGround -> greenCenter GPS coordinates (repo owner surveyed every
  // tee via Google Earth) — survey-grade, not an estimate, for all 18
  // holes. This replaced two earlier wrong sources in turn: the number
  // StrackaLine prints next to the compass rose (looked like a real
  // per-hole azimuth, but on-course testing showed hole 1 and hole 3 play
  // roughly opposite directions while their printed numbers are only 7°
  // apart — it isn't tee-to-green direction), then a compass-rose-rotation
  // estimate (±15-20° typical, though holes 15 and 17 were off by ~24°,
  // both since superseded by the real bearing here). Still just a fallback
  // for when live GPS bearing isn't available yet — see teeGround/
  // greenCenter below for the raw survey points.
  {
    courseId: "BrentwoodCC",
    hole: 1,
    // Exact bearing computed from teeGround -> greenCenter (real GPS points,
    // supersedes the ±15-20° compass-derived estimate of 153).
    holeAzimuthDeg: 141.1,
    // Verified via Google Earth: 34°02'37.35"N 118°28'55.68"W
    greenCenter: { lat: 34.04371, lng: -118.48213 },
    teeGround: { lat: 34.0458987, lng: -118.4842584 },
    layup: { lat: 34.0728, lng: -118.4898 },
  },
  {
    courseId: "BrentwoodCC",
    hole: 2,
    // Exact bearing from teeGround -> greenCenter (compass-derived estimate
    // was 36, within 3° — good cross-check).
    holeAzimuthDeg: 39.0,
    // Original DMS: 34°02'39.82"N 118°28'51.10"W
    greenCenter: { lat: 34.04439, lng: -118.48086 },
    teeGround: { lat: 34.0433479, lng: -118.4818785 },
  },
  {
    courseId: "BrentwoodCC",
    hole: 3,
    // Exact bearing from teeGround -> greenCenter (compass-derived estimate
    // was 312, within 9°).
    holeAzimuthDeg: 303.4,
    // Original DMS: 34°02'45.65"N 118°28'59.78"W
    greenCenter: { lat: 34.04601, lng: -118.48327 },
    teeGround: { lat: 34.0445177, lng: -118.4805383 },
  },
  {
    courseId: "BrentwoodCC",
    hole: 4,
    // Exact bearing from teeGround -> greenCenter (compass-derived estimate
    // was 69, within 1° — excellent cross-check).
    holeAzimuthDeg: 68.4,
    // Original DMS: 34°02'50.40"N 118°28'42.62"W
    // NOTE: this matches the Google Earth screenshot labeled "5th green" —
    // verify whether this is actually hole 4 or hole 5.
    greenCenter: { lat: 34.04733, lng: -118.47851 },
    teeGround: { lat: 34.0461177, lng: -118.4822076 },
  },
  {
    courseId: "BrentwoodCC",
    hole: 5,
    // Exact bearing from teeGround -> greenCenter (compass-derived estimate
    // was 34, within 16°).
    holeAzimuthDeg: 49.8,
    // Verified via Google Earth: 34°02'56.98"N 118°28'30.32"W
    greenCenter: { lat: 34.04916, lng: -118.47509 },
    teeGround: { lat: 34.0464601, lng: -118.4789457 },
  },
  {
    courseId: "BrentwoodCC",
    hole: 6,
    // Exact bearing from teeGround -> greenCenter (compass-derived estimate
    // was 274, within 11°).
    holeAzimuthDeg: 285.2,
    // Original DMS: 34°03'02.05"N 118°28'42.23"W
    // Matches "Untitled placemark" in Google Earth screenshot.
    greenCenter: { lat: 34.05057, lng: -118.47840 },
    teeGround: { lat: 34.0498337, lng: -118.4751344 },
  },
  {
    courseId: "BrentwoodCC",
    hole: 7,
    // Exact bearing from teeGround -> greenCenter (compass-derived estimate
    // was 264, within 11°).
    holeAzimuthDeg: 252.9,
    // Original DMS: 34°03'00.72"N 118°28'48.31"W
    greenCenter: { lat: 34.05020, lng: -118.48009 },
    teeGround: { lat: 34.0505396, lng: -118.4787559 },
  },
  {
    courseId: "BrentwoodCC",
    hole: 8,
    // Exact bearing from teeGround -> greenCenter (compass-derived estimate
    // was 265, within 10°).
    holeAzimuthDeg: 254.8,
    // Original DMS: 34°02'56.71"N 118°29'06.81"W
    // Matches prior known-good value — good confirmation.
    greenCenter: { lat: 34.04909, lng: -118.48523 },
    teeGround: { lat: 34.0501495, lng: -118.4805225 },
  },
  {
    courseId: "BrentwoodCC",
    hole: 9,
    // Exact bearing from teeGround -> greenCenter (compass-derived estimate
    // was 175, within 16°).
    holeAzimuthDeg: 159.4,
    // Verified via Google Earth: 34°02'47.92"N 118°29'04.32"W
    greenCenter: { lat: 34.04664, lng: -118.48453 },
    teeGround: { lat: 34.0492542, lng: -118.4857158 },
  },
  {
    courseId: "BrentwoodCC",
    hole: 10,
    // Exact bearing from teeGround -> greenCenter (compass-derived estimate
    // was 70, within 4°).
    holeAzimuthDeg: 66.3,
    // Original DMS: 34°02'51.07"N 118°28'49.67"W
    greenCenter: { lat: 34.04752, lng: -118.48046 },
    teeGround: { lat: 34.0462303, lng: -118.4840104 },
  },
  {
    courseId: "BrentwoodCC",
    hole: 11,
    // Exact bearing from teeGround -> greenCenter (compass-derived estimate
    // was 49, within 15°).
    holeAzimuthDeg: 63.6,
    // Original DMS: 34°02'55.63"N 118°28'34.96"W
    greenCenter: { lat: 34.04879, lng: -118.47638 },
    teeGround: { lat: 34.0473701, lng: -118.4798254 },
  },
  {
    courseId: "BrentwoodCC",
    hole: 12,
    // Exact bearing from teeGround -> greenCenter (compass-derived estimate
    // was 247, within 3°).
    holeAzimuthDeg: 243.9,
    // Original DMS: 34°02'52.33"N 118°28'48.60"W
    greenCenter: { lat: 34.04787, lng: -118.48017 },
    teeGround: { lat: 34.0496617, lng: -118.4757493 },
  },
  {
    courseId: "BrentwoodCC",
    hole: 13,
    // Exact bearing from teeGround -> greenCenter (compass-derived estimate
    // was 276, within 10°).
    holeAzimuthDeg: 265.6,
    // Original DMS: 34°02'53.33"N 118°29'04.02"W
    greenCenter: { lat: 34.04815, lng: -118.48445 },
    teeGround: { lat: 34.0483824, lng: -118.4807739 },
  },
  {
    courseId: "BrentwoodCC",
    hole: 14,
    // Exact bearing from teeGround -> greenCenter (compass-derived estimate
    // was 61, within 11°).
    holeAzimuthDeg: 71.5,
    // Original DMS: 34°03'00.01"N 118°28'45.21"W
    greenCenter: { lat: 34.05000, lng: -118.47922 },
    teeGround: { lat: 34.0486096, lng: -118.4842356 },
  },
  {
    courseId: "BrentwoodCC",
    hole: 15,
    // Exact bearing from teeGround -> greenCenter. Compass-derived estimate
    // (64) was off by 24 here — one of two outliers in the whole set.
    holeAzimuthDeg: 87.9,
    // Original DMS: 34°03'00.78"N 118°28'36.10"W
    greenCenter: { lat: 34.05022, lng: -118.47669 },
    teeGround: { lat: 34.0501580, lng: -118.4786925 },
  },
  {
    courseId: "BrentwoodCC",
    hole: 16,
    // Exact bearing from teeGround -> greenCenter (compass-derived estimate
    // was 257, within 2° — excellent cross-check).
    holeAzimuthDeg: 254.7,
    // Original DMS: 34°02'56.36"N 118°28'48.09"W
    greenCenter: { lat: 34.04899, lng: -118.48003 },
    teeGround: { lat: 34.0498070, lng: -118.4764280 },
  },
  {
    courseId: "BrentwoodCC",
    hole: 17,
    // Exact bearing from teeGround -> greenCenter. Compass-derived estimate
    // (261) was off by 24 here — the other outlier in the set.
    holeAzimuthDeg: 237.4,
    // Original DMS: 34°02'55.54"N 118°28'51.67"W
    greenCenter: { lat: 34.04876, lng: -118.48102 },
    teeGround: { lat: 34.0494223, lng: -118.4797697 },
  },
  {
    courseId: "BrentwoodCC",
    hole: 18,
    // Exact bearing from teeGround -> greenCenter (compass-derived estimate
    // was 245, within 1° — excellent cross-check).
    holeAzimuthDeg: 244.4,
    // Original DMS: 34°02'48.56"N 118°29'02.22"W
    greenCenter: { lat: 34.04682, lng: -118.48395 },
    teeGround: { lat: 34.0480531, lng: -118.4808464 },
  },

  // ── Woodland Hills Country Club ──────────────────────────────────────────
  // Green centers from Google Earth KML (June 2026).
  // holeAzimuthDeg is APPROXIMATE — derived by reading the rotation of the
  // compass-rose icon printed on each hole's StrackaLine green book page
  // (same method as WaccabucCC below), accurate to roughly ±15-20°. Replaces
  // the earlier placeholder-0 values. Tap-to-pin still falls back to live
  // GPS bearing on-course; this is a convenience default.
  { courseId: "WoodlandHillsCC", hole: 1,  holeAzimuthDeg: 255, greenCenter: { lat: 34.15531, lng: -118.59480 } },
  { courseId: "WoodlandHillsCC", hole: 2,  holeAzimuthDeg: 255, greenCenter: { lat: 34.15545, lng: -118.59567 } },
  { courseId: "WoodlandHillsCC", hole: 3,  holeAzimuthDeg: 352, greenCenter: { lat: 34.15784, lng: -118.59209 } },
  { courseId: "WoodlandHillsCC", hole: 4,  holeAzimuthDeg: 160, greenCenter: { lat: 34.15633, lng: -118.59592 } },
  { courseId: "WoodlandHillsCC", hole: 5,  holeAzimuthDeg: 245, greenCenter: { lat: 34.15399, lng: -118.59593 } },
  { courseId: "WoodlandHillsCC", hole: 6,  holeAzimuthDeg: 95,  greenCenter: { lat: 34.15367, lng: -118.59817 } },
  { courseId: "WoodlandHillsCC", hole: 7,  holeAzimuthDeg: 90,  greenCenter: { lat: 34.15309, lng: -118.59640 } },
  { courseId: "WoodlandHillsCC", hole: 8,  holeAzimuthDeg: 95,  greenCenter: { lat: 34.15466, lng: -118.59462 } },
  { courseId: "WoodlandHillsCC", hole: 9,  holeAzimuthDeg: 170, greenCenter: { lat: 34.15623, lng: -118.59225 } },
  { courseId: "WoodlandHillsCC", hole: 10, holeAzimuthDeg: 170, greenCenter: { lat: 34.15421, lng: -118.59411 } },
  { courseId: "WoodlandHillsCC", hole: 11, holeAzimuthDeg: 162, greenCenter: { lat: 34.15150, lng: -118.59458 } },
  { courseId: "WoodlandHillsCC", hole: 12, holeAzimuthDeg: 355, greenCenter: { lat: 34.15076, lng: -118.59440 } },
  { courseId: "WoodlandHillsCC", hole: 13, holeAzimuthDeg: 170, greenCenter: { lat: 34.15392, lng: -118.59395 } },
  { courseId: "WoodlandHillsCC", hole: 14, holeAzimuthDeg: 130, greenCenter: { lat: 34.15116, lng: -118.59347 } },
  { courseId: "WoodlandHillsCC", hole: 15, holeAzimuthDeg: 355, greenCenter: { lat: 34.15025, lng: -118.59261 } },
  { courseId: "WoodlandHillsCC", hole: 16, holeAzimuthDeg: 160, greenCenter: { lat: 34.15394, lng: -118.59318 } },
  { courseId: "WoodlandHillsCC", hole: 17, holeAzimuthDeg: 355, greenCenter: { lat: 34.15168, lng: -118.59150 } },
  { courseId: "WoodlandHillsCC", hole: 18, holeAzimuthDeg: 0,   greenCenter: { lat: 34.15642, lng: -118.59079 } },

  // ── Waccabuc Country Club ────────────────────────────────────────────────
  // Green centers from Google Earth placemarks (Aug 2026), decimal degrees.
  // holeAzimuthDeg is now the EXACT bearing computed from real teeGround ->
  // greenCenter GPS coordinates (KML export, Sep 2026) — survey-grade,
  // replacing the earlier compass-rotation estimate for all 18 holes. Most
  // estimates were within 20° of the real bearing (consistent with the
  // ±15-20° margin already established for that method elsewhere). Holes 6
  // and 9's tee positions were revised again after on-course review with a
  // member (second KML, still Sep 2026) — hole 9 in particular had looked
  // like a ~120° miss against the first KML's tee point, confirmed here as
  // a bad tee placemark rather than a compass misread on my end.
  { courseId: "WaccabucCC", hole: 1,  holeAzimuthDeg: 255.8, greenCenter: { lat: 41.2886959, lng: -73.5990294 }, teeGround: { lat: 41.2892941, lng: -73.5958860 } },
  { courseId: "WaccabucCC", hole: 2,  holeAzimuthDeg: 337.0, greenCenter: { lat: 41.2923534, lng: -73.6013739 }, teeGround: { lat: 41.2892316, lng: -73.5996088 } },
  { courseId: "WaccabucCC", hole: 3,  holeAzimuthDeg: 162.4, greenCenter: { lat: 41.2892313, lng: -73.6005197 }, teeGround: { lat: 41.2923773, lng: -73.6018471 } },
  { courseId: "WaccabucCC", hole: 4,  holeAzimuthDeg: 329.1, greenCenter: { lat: 41.2910741, lng: -73.6023072 }, teeGround: { lat: 41.2890184, lng: -73.6006685 } },
  { courseId: "WaccabucCC", hole: 5,  holeAzimuthDeg: 300.2, greenCenter: { lat: 41.2918107, lng: -73.6053796 }, teeGround: { lat: 41.2911398, lng: -73.6038438 } },
  { courseId: "WaccabucCC", hole: 6,  holeAzimuthDeg: 143.2, greenCenter: { lat: 41.2883853, lng: -73.6023382 }, teeGround: { lat: 41.2914120, lng: -73.6053483 } },
  { courseId: "WaccabucCC", hole: 7,  holeAzimuthDeg: 345.9, greenCenter: { lat: 41.2894959, lng: -73.6023295 }, teeGround: { lat: 41.2884212, lng: -73.6019691 } },
  { courseId: "WaccabucCC", hole: 8,  holeAzimuthDeg: 141.1, greenCenter: { lat: 41.2876088, lng: -73.5998244 }, teeGround: { lat: 41.2893265, lng: -73.6016677 } },
  { courseId: "WaccabucCC", hole: 9,  holeAzimuthDeg: 299.5, greenCenter: { lat: 41.2886798, lng: -73.5996354 }, teeGround: { lat: 41.2882148, lng: -73.5985426 } },
  { courseId: "WaccabucCC", hole: 10, holeAzimuthDeg: 74.6,  greenCenter: { lat: 41.2901292, lng: -73.5899107 }, teeGround: { lat: 41.2891760, lng: -73.5945026 } },
  { courseId: "WaccabucCC", hole: 11, holeAzimuthDeg: 71.4,  greenCenter: { lat: 41.2904187, lng: -73.5862499 }, teeGround: { lat: 41.2894023, lng: -73.5902640 } },
  { courseId: "WaccabucCC", hole: 12, holeAzimuthDeg: 235.5, greenCenter: { lat: 41.2892830, lng: -73.5875470 }, teeGround: { lat: 41.2901066, lng: -73.5859504 } },
  { courseId: "WaccabucCC", hole: 13, holeAzimuthDeg: 145.0, greenCenter: { lat: 41.2856789, lng: -73.5842088 }, teeGround: { lat: 41.2886078, lng: -73.5869365 } },
  { courseId: "WaccabucCC", hole: 14, holeAzimuthDeg: 333.6, greenCenter: { lat: 41.2895081, lng: -73.5862575 }, teeGround: { lat: 41.2858445, lng: -73.5838341 } },
  { courseId: "WaccabucCC", hole: 15, holeAzimuthDeg: 144.1, greenCenter: { lat: 41.2885936, lng: -73.5847749 }, teeGround: { lat: 41.2895674, lng: -73.5857131 } },
  { courseId: "WaccabucCC", hole: 16, holeAzimuthDeg: 338.6, greenCenter: { lat: 41.2907086, lng: -73.5854188 }, teeGround: { lat: 41.2884790, lng: -73.5842572 } },
  { courseId: "WaccabucCC", hole: 17, holeAzimuthDeg: 266.6, greenCenter: { lat: 41.2906870, lng: -73.5903258 }, teeGround: { lat: 41.2908797, lng: -73.5859908 } },
  { courseId: "WaccabucCC", hole: 18, holeAzimuthDeg: 259.8, greenCenter: { lat: 41.2900816, lng: -73.5948350 }, teeGround: { lat: 41.2906274, lng: -73.5907985 } },
];

export function toPinBucket(
  pinDepth: "front" | "middle" | "back",
  pinLateral: "left" | "center" | "right"
): PinBucket {
  return `${pinDepth}-${pinLateral}` as PinBucket;
}

export function getHoleTargetPreset(
  courseId: string,
  hole: number
): HoleTargetPreset | null {
  return (
    HOLE_TARGET_PRESETS.find(
      (p) => p.courseId === courseId && p.hole === hole
    ) ?? null
  );
}

// Approximate green dimensions for offset calculation.
// Each third of a ~25yd-deep × ~20yd-wide green.
const DEPTH_OFFSET_YDS = 8;
const LATERAL_OFFSET_YDS = 6;
const METERS_PER_YARD = 0.9144;
const EARTH_RADIUS_M = 6371000;

export function offsetLatLng(origin: LatLng, bearingDegrees: number, distanceYards: number): LatLng {
  const d = distanceYards * METERS_PER_YARD;
  const θ = (bearingDegrees * Math.PI) / 180;
  const φ1 = (origin.lat * Math.PI) / 180;
  const λ1 = (origin.lng * Math.PI) / 180;

  const φ2 = Math.asin(
    Math.sin(φ1) * Math.cos(d / EARTH_RADIUS_M) +
    Math.cos(φ1) * Math.sin(d / EARTH_RADIUS_M) * Math.cos(θ)
  );
  const λ2 =
    λ1 +
    Math.atan2(
      Math.sin(θ) * Math.sin(d / EARTH_RADIUS_M) * Math.cos(φ1),
      Math.cos(d / EARTH_RADIUS_M) - Math.sin(φ1) * Math.sin(φ2)
    );

  return { lat: (φ2 * 180) / Math.PI, lng: (λ2 * 180) / Math.PI };
}

// Core conversion shared by both pin-entry methods below: a forward/right
// offset (in feet, relative to green center, forward = deeper into the
// green away from the approach, right = right when facing the green) plus
// the hole's azimuth, converted to a real-world lat/lng.
function greenOffsetFtToLatLng(params: {
  ftForward: number;
  ftRight: number;
  greenCenter: LatLng;
  holeAzimuthDeg: number;
}): LatLng {
  const { ftForward, ftRight, greenCenter, holeAzimuthDeg } = params;

  const azRad = (holeAzimuthDeg * Math.PI) / 180;
  // Inverse rotation of gpsOffsetToImageNorm's forward transform.
  const ftNorth = ftForward * Math.cos(azRad) - ftRight * Math.sin(azRad);
  const ftEast = ftForward * Math.sin(azRad) + ftRight * Math.cos(azRad);

  const distanceFt = Math.sqrt(ftNorth * ftNorth + ftEast * ftEast);
  const distanceYards = distanceFt / 3;
  const bearingDegrees = (Math.atan2(ftEast, ftNorth) * 180) / Math.PI;

  if (distanceYards < 0.01) return { ...greenCenter };

  return offsetLatLng(greenCenter, (bearingDegrees + 360) % 360, distanceYards);
}

// Inverse of gpsOffsetToImageNorm (src/lib/greenVectors.ts): converts a
// normalized tap position on the green image back into a real-world lat/lng,
// using the hole's green center + azimuth. Used by the pre-round pin-sheet
// setup screen to turn a tapped pin position into a coordinate the rest of
// the targeting math (yardage, club selection, EV) can consume directly.
export function imageNormToLatLng(params: {
  xNorm: number;
  yNorm: number;
  greenCenter: LatLng;
  holeAzimuthDeg: number;
  greenHalfWidthFt?: number;
  greenHalfDepthFt?: number;
}): LatLng {
  const {
    xNorm,
    yNorm,
    greenCenter,
    holeAzimuthDeg,
    greenHalfWidthFt = 45,
    greenHalfDepthFt = 45,
  } = params;

  const ftForward = (0.5 - yNorm) * (2 * greenHalfDepthFt);
  const ftRight = (xNorm - 0.5) * (2 * greenHalfWidthFt);

  return greenOffsetFtToLatLng({ ftForward, ftRight, greenCenter, holeAzimuthDeg });
}

// A single greenHalfWidthFt assumes the green is a rectangle, but real
// greens taper and their centerline curves -- confirmed against EZLocator
// ground-truth screenshots on Brentwood holes 3/5/16, where a single
// half-width was off by as much as 15-20ft at some pin depths (too wide at
// a green's narrow tip, too narrow at its wide middle) and the centerline
// itself shifted by 5+ yards front-to-back. WidthProfile replaces the flat
// constant with samples of the green's actual shape, traced from
// EZLocator's own (to-scale) boundary rendering: frac=0 is the green's
// very front tip, frac=1 is its very back tip, halfWidthFt is the local
// cross-section half-width at that depth, centerOffsetFt is how far the
// local centerline sits from the green's OVERALL bbox center (+ = right).
// See tools/vectorizer/extract_width_profile_from_ezlocator.py.
export type WidthProfilePoint = {
  frac: number;
  halfWidthFt: number;
  centerOffsetFt: number;
};

// Linearly interpolates a WidthProfile at a given front-to-back fraction
// (clamped to the profile's own range, which should always be [0,1]).
// Points must be sorted ascending by frac.
export function interpolateWidthProfile(
  profile: WidthProfilePoint[],
  frac: number
): { halfWidthFt: number; centerOffsetFt: number } {
  const clamped = Math.max(profile[0].frac, Math.min(profile[profile.length - 1].frac, frac));
  for (let i = 0; i < profile.length - 1; i++) {
    const a = profile[i];
    const b = profile[i + 1];
    if (clamped >= a.frac && clamped <= b.frac) {
      const t = b.frac === a.frac ? 0 : (clamped - a.frac) / (b.frac - a.frac);
      return {
        halfWidthFt: a.halfWidthFt + t * (b.halfWidthFt - a.halfWidthFt),
        centerOffsetFt: a.centerOffsetFt + t * (b.centerOffsetFt - a.centerOffsetFt),
      };
    }
  }
  const last = profile[profile.length - 1];
  return { halfWidthFt: last.halfWidthFt, centerOffsetFt: last.centerOffsetFt };
}

// Alternative pin entry: distance in from the front edge of the green, plus
// a left/right offset from the green's centerline — the format most paper
// pin sheets actually use (e.g. "24F 6R"), so it can be transcribed directly
// without needing to judge a precise tap position on a small image.
//
// lateralFt is the distance from the NEAREST edge on that side, not from
// the centerline (confirmed against EZLocator, the pin-locator tool these
// numbers are usually transcribed from) -- e.g. "7R" on a green with a
// 14ft half-width means the pin is 14-7=7ft right of center, not 7ft.
export function frontOffsetToLatLng(params: {
  distanceFromFrontFt: number;
  lateralFt: number;
  lateralSide: "left" | "right";
  greenCenter: LatLng;
  holeAzimuthDeg: number;
  greenHalfDepthFt?: number;
  greenHalfWidthFt?: number;
  widthProfile?: WidthProfilePoint[];
}): LatLng {
  const {
    distanceFromFrontFt,
    lateralFt,
    lateralSide,
    greenCenter,
    holeAzimuthDeg,
    greenHalfDepthFt = 45,
    greenHalfWidthFt = 45,
    widthProfile,
  } = params;

  // Front edge of the green is the approach side, at ftForward = -greenHalfDepthFt.
  const ftForward = -greenHalfDepthFt + distanceFromFrontFt;

  let ftRight: number;
  if (widthProfile && widthProfile.length >= 2) {
    const frac = Math.max(0, Math.min(1, distanceFromFrontFt / (2 * greenHalfDepthFt)));
    const { halfWidthFt, centerOffsetFt } = interpolateWidthProfile(widthProfile, frac);
    ftRight = centerOffsetFt + (lateralSide === "right" ? 1 : -1) * Math.max(0, halfWidthFt - lateralFt);
  } else {
    // Clamp at 0, not just subtract: if the entered from-edge distance
    // exceeds the stored half-width (a green measured narrower than it
    // really is -- confirmed to happen, see README), greenHalfWidthFt -
    // lateralFt goes negative and silently flips the pin to the WRONG SIDE
    // of center instead of just landing imprecisely near it.
    ftRight = (lateralSide === "right" ? 1 : -1) * Math.max(0, greenHalfWidthFt - lateralFt);
  }

  return greenOffsetFtToLatLng({ ftForward, ftRight, greenCenter, holeAzimuthDeg });
}

// Offset green center toward the approximate pin position using the player's
// approach bearing. "Front" = toward the player, "back" = away. "Left"/"right"
// are from the player's perspective facing the green.
function computePinOffset(
  greenCenter: LatLng,
  approachBearingDeg: number,
  pinDepth: "front" | "middle" | "back",
  pinLateral: "left" | "center" | "right"
): LatLng {
  let result = { ...greenCenter };

  if (pinDepth === "front") {
    // Front = toward player = opposite of approach direction
    result = offsetLatLng(result, (approachBearingDeg + 180) % 360, DEPTH_OFFSET_YDS);
  } else if (pinDepth === "back") {
    result = offsetLatLng(result, approachBearingDeg, DEPTH_OFFSET_YDS);
  }

  if (pinLateral === "left") {
    // Left from player facing the green = approachBearing - 90°
    result = offsetLatLng(result, (approachBearingDeg - 90 + 360) % 360, LATERAL_OFFSET_YDS);
  } else if (pinLateral === "right") {
    result = offsetLatLng(result, (approachBearingDeg + 90) % 360, LATERAL_OFFSET_YDS);
  }

  return result;
}

export function resolveTargetCoordinates(args: {
  courseId: string;
  hole: number;
  targetType: TargetType;
  pinDepth: "front" | "middle" | "back";
  pinLateral: "left" | "center" | "right";
  playerLat?: number | null;
  playerLng?: number | null;
}): LatLng | null {
  const preset = getHoleTargetPreset(args.courseId, args.hole);
  if (!preset) return null;

  if (args.targetType === "center") return preset.greenCenter ?? null;
  if (args.targetType === "layup") return preset.layup ?? null;

  // Use an explicit survey coordinate if available for this pin bucket.
  const bucket = toPinBucket(args.pinDepth, args.pinLateral);
  const explicit = preset.pinBuckets?.[bucket];
  if (explicit) return explicit;

  if (!preset.greenCenter) return null;

  // Compute approach bearing: player position is most accurate; fall back to
  // preset azimuth; fall back to green center when nothing is available.
  let approachBearing: number | null = null;
  if (args.playerLat != null && args.playerLng != null) {
    approachBearing = bearingDeg(args.playerLat, args.playerLng, preset.greenCenter.lat, preset.greenCenter.lng);
  } else if (preset.holeAzimuthDeg != null) {
    approachBearing = preset.holeAzimuthDeg;
  }

  if (approachBearing == null) return preset.greenCenter;

  return computePinOffset(preset.greenCenter, approachBearing, args.pinDepth, args.pinLateral);
}
